import { useState } from "react";
import { LuPalette, LuX } from "react-icons/lu";
import { colors, effects, typography, components } from "../../design/tokens";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";

interface AttachedDesignChipProps {
  designName: string;
  description: string;
  onRemove: () => void;
  iconOnly?: boolean;
}

export function AttachedDesignChip({
  designName,
  description,
  onRemove,
  iconOnly = false,
}: AttachedDesignChipProps) {
  const isCompactView = useIsCompactView();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);

  const handleChipClick = () => {
    if (isCompactView || iconOnly) {
      setShowMobileModal(true);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  // Icon-only variant for mobile - pill shape with inline close button
  if (iconOnly) {
    return (
      <>
        <div
          className={`flex items-center gap-1.5 h-9 pl-2 pr-1
            bg-dark-surface-tertiary border border-dark-border
            ${effects.borderRadius.full}
            cursor-pointer ${effects.transition.default}`}
          onClick={handleChipClick}
          aria-label={designName}
        >
          <LuPalette className="w-4 h-4 text-primary-pink" />
          <button
            onClick={handleRemove}
            className={`p-1 rounded-full ${colors.hover.brightness} ${effects.transition.default}`}
            aria-label="Remove design"
          >
            <LuX className="w-3.5 h-3.5 text-dark-text-secondary" />
          </button>
        </div>

        {/* Mobile Modal */}
        {showMobileModal && (
          <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4
              ${components.modal.overlay}`}
            onClick={() => setShowMobileModal(false)}
          >
            <div
              className={`w-full max-w-md p-4
                ${colors.surface.primary} border border-dark-border
                ${effects.borderRadius.lg}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <LuPalette className={`w-5 h-5 ${colors.text.secondary}`} />
                  <span className={`${typography.size.base} ${typography.weight.semibold} ${colors.text.primary}`}>
                    {designName}
                  </span>
                </div>
                <button
                  onClick={() => setShowMobileModal(false)}
                  className={`${components.modal.closeButton}`}
                >
                  <LuX className="w-5 h-5" />
                </button>
              </div>
              <div className="border-t border-dark-border pt-3">
                <p className={`${typography.size.sm} ${colors.text.secondary} leading-relaxed`}>
                  {description}
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Regular variant (desktop)
  return (
    <>
      <div
        className="relative"
        onMouseEnter={() => !isCompactView && setShowTooltip(true)}
        onMouseLeave={() => !isCompactView && setShowTooltip(false)}
      >
        <div
          onClick={handleChipClick}
          className={`flex items-center gap-2 px-3 h-9
            bg-dark-surface-tertiary border border-dark-border
            ${effects.borderRadius.full}
            ${isCompactView ? "cursor-pointer" : ""}`}
        >
          <LuPalette className={`w-4 h-4 ${colors.text.secondary}`} />
          <span className={`${typography.size.sm} ${colors.text.primary}`}>
            {designName}
          </span>
          <button
            onClick={handleRemove}
            className={`${colors.text.secondary} hover:text-dark-text ${effects.transition.default}`}
          >
            <LuX className="w-4 h-4" />
          </button>
        </div>

        {/* Desktop Tooltip - Wide horizontal layout */}
        {showTooltip && !isCompactView && (
          <div
            className={`absolute bottom-full left-0 mb-2 z-50
              w-[500px] p-4
              ${colors.surface.primary} border border-dark-border
              ${effects.borderRadius.lg}
              shadow-lg`}
          >
            <div className="flex items-center gap-2 mb-3">
              <LuPalette className={`w-4 h-4 ${colors.text.secondary}`} />
              <span className={`${typography.size.base} ${typography.weight.semibold} ${colors.text.primary}`}>
                {designName}
              </span>
            </div>
            <div className="border-t border-dark-border pt-3">
              <p className={`${typography.size.sm} ${colors.text.secondary} leading-relaxed`}>
                {description}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Modal */}
      {showMobileModal && isCompactView && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4
            ${components.modal.overlay}`}
          onClick={() => setShowMobileModal(false)}
        >
          <div
            className={`w-full max-w-md p-4
              ${colors.surface.primary} border border-dark-border
              ${effects.borderRadius.lg}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <LuPalette className={`w-5 h-5 ${colors.text.secondary}`} />
                <span className={`${typography.size.base} ${typography.weight.semibold} ${colors.text.primary}`}>
                  {designName}
                </span>
              </div>
              <button
                onClick={() => setShowMobileModal(false)}
                className={`${components.modal.closeButton}`}
              >
                <LuX className="w-5 h-5" />
              </button>
            </div>
            <div className="border-t border-dark-border pt-3">
              <p className={`${typography.size.sm} ${colors.text.secondary} leading-relaxed`}>
                {description}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
